import { CheckWithdrawalRequest } from '../../api/useCreateCheckWithdrawal'
import { convertToValidDate } from '../../utils'
import { getFrequencyInterval } from '../ACHTransferForm/utils/getFrequencyInterval'
import { getFrequencyUnit } from '../ACHTransferForm/utils/getFrequencyUnit'
import { CountryItem } from '../../reducers/types'
import { generateExternalTransferId, getCountryCode } from '../../utils/payloadHelpers'
import { CheckTransferValues } from '../../containers/CheckTransfers/Types'
import isEmpty from 'lodash.isempty'
import { getFeeOverrideDetails } from '../TransferFees/utils/getFeeOverrideDetails'

export const formatCheckWithdrawal = (
  account: string,
  {
    beneficiaryName,
    deliveryMethod,
    distributionReason,
    federalTaxWithholdingAmountRaw,
    federalTaxWithholdingType,
    memo1,
    memo2,
    memo3,
    recipientAddress,
    recipientAddressLine2,
    recipientCity,
    recipientState,
    recipientZip,
    recipientCountry,
    sendTo,
    stateTaxWithholdingAmountRaw,
    stateTaxWithholdingType,
    totalBalanceCheckbox,
    transferAmountRaw,
    receivingInstitutionName,
    transferStartDate,
    transferRecurrence,
    transferFrequency,
    numberOfTransfers,
    transferScheduledDate,
    whenToSend,
    fees,
    optOutFormProvided
  }: CheckTransferValues,
  countryList: CountryItem[],
  isScheduledTransfer: boolean
) => {
  const request: CheckWithdrawalRequest = {
    externalId: generateExternalTransferId(),
    account,
    deliveryMethod: deliveryMethod!,
    beneficiaryDetails: {},
    memos: [],
    ...(totalBalanceCheckbox && { isFullDisbursement: true }),
    ...(!totalBalanceCheckbox && { amount: transferAmountRaw }),
    ...(isScheduledTransfer && {
      ...(transferRecurrence === 'RECURRING' && {
        startDate: convertToValidDate(transferStartDate),
        frequencyUnit: getFrequencyUnit(transferFrequency!),
        frequencyInterval: getFrequencyInterval(transferFrequency!),
        ...(numberOfTransfers &&
          Number(numberOfTransfers) > 0 && {
            occurrences: Number(numberOfTransfers)
          })
      }),
      ...(transferRecurrence === 'ONE_TIME' && {
        startDate: convertToValidDate(transferScheduledDate),
        frequencyUnit: 'DAY',
        frequencyInterval: 1,
        ...(whenToSend === 'IN_THE_FUTURE' && { occurrences: 1 }),
        ...(whenToSend === 'IMMEDIATELY' &&
          numberOfTransfers &&
          Number(numberOfTransfers) > 0 && {
            occurrences: Number(numberOfTransfers)
          })
      })
    }),
    ...(!isEmpty(fees) && { feeOverrideDetails: getFeeOverrideDetails(fees) })
  }

  if (sendTo !== 'ACCOUNT-HOLDER') {
    const streetAddress = recipientAddressLine2 ? [recipientAddress, recipientAddressLine2] : [recipientAddress]

    request.beneficiaryDetails.addressDetails = {
      streetAddress,
      city: recipientCity,
      ...(recipientState && { state: recipientState }),
      country: getCountryCode(countryList, recipientCountry),
      ...(recipientZip && { postalCode: recipientZip })
    }
  }

  if (sendTo === 'THIRD-PARTY') {
    request.beneficiaryDetails.name = beneficiaryName
    request.beneficiaryDetails.isThirdParty = true
  }

  if (distributionReason) {
    request.iraDistributionDetails = {
      distributionReason,
      ...(distributionReason === 'TRANSFER' && { receivingInstitutionName })
    }
    if (federalTaxWithholdingType) {
      request.iraDistributionDetails.federalTaxWithholding = {
        valueType: federalTaxWithholdingType,
        value: federalTaxWithholdingAmountRaw
      }
    }
    if (stateTaxWithholdingType) {
      request.iraDistributionDetails.stateTaxWithholding = {
        valueType: stateTaxWithholdingType,
        value: stateTaxWithholdingAmountRaw
      }
    }
    if (optOutFormProvided) {
      request.iraDistributionDetails.hasStateWithholdingWaiver = optOutFormProvided
    }
  }

  if (memo1 || memo2 || memo3) {
    request.memos = [memo1, memo2, memo3].map((memo) => (memo ? memo : null))
  }
  return request
}
